import { z } from 'zod';

/**
 * Период отчёта — московские дни, обе границы включительно, как на сервере
 * (`ReportPeriodQuery`). Живёт в адресе (`?from=&to=`): ссылку на «прошлый
 * месяц по менеджерам» можно переслать, и «назад» возвращает в тот же отбор.
 */

export interface Period {
  from: string;
  to: string;
}

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const PeriodSearch = z.object({
  from: Day.optional().catch(undefined),
  to: Day.optional().catch(undefined),
});
export type PeriodSearch = z.infer<typeof PeriodSearch>;

export function mskToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 3 * 3_600_000).toISOString().slice(0, 10);
}

export function addDays(day: string, amount: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + amount * 86_400_000).toISOString().slice(0, 10);
}

export function addMonths(month: string, amount: number): string {
  const date = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + amount, 1));
  return date.toISOString().slice(0, 10);
}

export function monthPeriod(month: string): Period {
  const first = `${month.slice(0, 7)}-01`;
  return { from: first, to: addDays(addMonths(first, 1), -1) };
}

export function currentMonth(now: Date = new Date()): Period {
  return monthPeriod(mskToday(now));
}

/** Период из адреса; неполный или перевёрнутый — текущий месяц. */
export function periodOf(search: PeriodSearch): Period {
  if (search.from !== undefined && search.to !== undefined && search.from <= search.to) {
    return { from: search.from, to: search.to };
  }
  return currentMonth();
}

/** Ровно один календарный месяц — тогда период листается стрелками. */
export function wholeMonth(period: Period): string | null {
  const month = monthPeriod(period.from);
  return month.from === period.from && month.to === period.to ? period.from : null;
}

export type PresetId = 'month' | 'previous' | 'quarter' | 'year';

export function preset(id: PresetId, now: Date = new Date()): Period {
  const today = mskToday(now);
  const thisMonth = `${today.slice(0, 7)}-01`;
  switch (id) {
    case 'month':
      return monthPeriod(thisMonth);
    case 'previous':
      return monthPeriod(addMonths(thisMonth, -1));
    case 'quarter':
      return { from: addMonths(thisMonth, -2), to: monthPeriod(thisMonth).to };
    case 'year':
      return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
  }
}

export function samePeriod(a: Period, b: Period): boolean {
  return a.from === b.from && a.to === b.to;
}

export function periodQuery(period: Period): string {
  return `from=${period.from}&to=${period.to}`;
}
